import React, { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { AddressLookup, type NormalisedAddress } from '@/features/address-lookup';
import { Form, Label, Button } from '@shared/components';
import { getFieldErrors } from '@/services/api';
import {
    holderContactDetailsSchema,
    type HolderContactDetailsFormInputs,
} from '../../schemas/customer.schemas';
import { computeCustomerDiff, type DiffEntry } from '../EditCustomerForm/computeCustomerDiff';
import { EditCustomerConfirmModal } from '../EditCustomerForm/EditCustomerConfirmModal';
import { CustomerAddressFields } from '../fields/CustomerAddressFields';
import { CustomerContactFields } from '../fields/CustomerContactFields';
import { CustomerCompanyFields } from '../fields/CustomerCompanyFields';
import { useUpdateCustomerMutation } from '../../hooks/useUpdateCustomerMutation';
import { diffToUpdateInput, wireFieldToFormField } from '../../utils/holderFieldMap';
import type { Customer, UpdateCustomerInput } from '../../types/customers.types';

interface Props {
    customer: Customer;
    agreementId: string;
    onCancel: () => void;
    onSaved: () => void;
}

function toDefaultValues(c: Customer): HolderContactDetailsFormInputs {
    return {
        addressLine1: c.address_line_1 ?? '',
        addressLine2: c.address_line_2 ?? '',
        city: c.city ?? '',
        postcode: c.postcode ?? '',
        email: c.email ?? '',
        mobilePhone: c.mobile_phone ?? '',
        businessName: c.business_name ?? '',
        vatNumber: c.vat_number ?? '',
    };
}

export const HolderContactDetailsForm: React.FC<Props> = ({
    customer,
    agreementId,
    onCancel,
    onSaved,
}) => {
    const [isConfirming, setIsConfirming] = useState(false);
    const [pendingDiff, setPendingDiff] = useState<DiffEntry[]>([]);
    const [isRetrievingAddress, setIsRetrievingAddress] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const mutation = useUpdateCustomerMutation(customer.id!);

    const { control, handleSubmit, setValue, setError, formState } =
        useForm<HolderContactDetailsFormInputs>({
            resolver: zodResolver(holderContactDetailsSchema),
            defaultValues: toDefaultValues(customer),
            mode: 'onBlur',
        });

    // Stable across renders — an unstable prop would re-fire effects in AddressLookup.
    const handleAddressSelected = useCallback(
        (address: NormalisedAddress) => {
            setValue('addressLine1', address.addressLine1, { shouldValidate: true, shouldDirty: true });
            setValue('addressLine2', address.addressLine2, { shouldValidate: true, shouldDirty: true });
            setValue('city', address.city, { shouldValidate: true, shouldDirty: true });
            setValue('postcode', address.postcode, { shouldValidate: true, shouldDirty: true });
        },
        [setValue],
    );

    const handleReviewChanges: SubmitHandler<HolderContactDetailsFormInputs> = (values) => {
        setSubmitError(null);
        const diff = computeCustomerDiff(customer, values);
        if (diff.length === 0) {
            onCancel();
            return;
        }
        setPendingDiff(diff);
        setIsConfirming(true);
    };

    const handleConfirm = async () => {
        setSubmitError(null);

        // Only the changed fields travel, so a concurrent edit to a field this
        // agent never touched is not clobbered.
        const input: UpdateCustomerInput = { ...diffToUpdateInput(pendingDiff), agreementId };

        try {
            await mutation.mutateAsync(input);
            setIsConfirming(false);
            onSaved();
        } catch (error) {
            const fieldErrors = Object.entries(getFieldErrors(error));

            fieldErrors.forEach(([wireField, messages]) => {
                const formField = wireFieldToFormField(wireField);
                if (formField) setError(formField, { message: messages[0] });
            });

            setSubmitError(
                fieldErrors.length > 0
                    ? 'Some fields could not be saved. Check the highlighted fields.'
                    : 'The change could not be saved. Your edits have been kept — try again.',
            );
            setIsConfirming(false);
        }
    };

    const handleCancelConfirm = () => {
        if (mutation.isPending) return;
        setIsConfirming(false);
    };

    return (
        <>
            <Form onSubmit={handleSubmit(handleReviewChanges)} className="space-y-4">
                <div>
                    <Label htmlFor="holderAddressFind">Find an address</Label>
                    <AddressLookup
                        id="holderAddressFind"
                        onAddressSelected={handleAddressSelected}
                        onRetrievePendingChange={setIsRetrievingAddress}
                    />
                </div>

                <CustomerAddressFields control={control} />
                <CustomerContactFields control={control} />
                <CustomerCompanyFields control={control} />

                {submitError && (
                    <p className="text-sm text-mp-error" role="alert">
                        {submitError}
                    </p>
                )}

                <div className="mt-4 flex justify-end gap-2">
                    <Button onClick={onCancel} variant="clear" type="button">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={
                            formState.isSubmitting ||
                            isRetrievingAddress ||
                            Object.keys(formState.errors).length > 0
                        }
                    >
                        Review changes
                    </Button>
                </div>
            </Form>

            <EditCustomerConfirmModal
                isOpen={isConfirming}
                diff={pendingDiff}
                isSubmitting={mutation.isPending}
                heading="Confirm the holder's new details"
                onCancel={handleCancelConfirm}
                onConfirm={handleConfirm}
            />
        </>
    );
};
